/**
 * Board representation and utilities
 */

import type { Piece, PieceType, Square, GameState } from './types';
import { PieceType as PT, Color as C } from './types';
import { getPieceFEN, pieceFromFEN } from './pieces';

/**
 * Creates an empty board (64 squares, all null)
 */
export function createEmptyBoard(): (Piece | null)[] {
  return Array(64).fill(null);
}

/**
 * Creates a board with the standard chess starting position
 */
export function createStartingBoard(): (Piece | null)[] {
  const board: (Piece | null)[] = createEmptyBoard();

  // White pieces (bottom, ranks 1-2)
  const whitePieces: PieceType[] = [PT.Rook, PT.Knight, PT.Bishop, PT.Queen, PT.King, PT.Bishop, PT.Knight, PT.Rook];
  for (let i = 0; i < 8; i++) {
    board[i] = { type: whitePieces[i], color: C.White }; // Rank 1
    board[i + 8] = { type: PT.Pawn, color: C.White };    // Rank 2
  }

  // Black pieces (top, ranks 7-8)
  for (let i = 0; i < 8; i++) {
    board[i + 48] = { type: PT.Pawn, color: C.Black };   // Rank 7
    board[i + 56] = { type: whitePieces[i], color: C.Black }; // Rank 8
  }

  return board;
}

/**
 * Creates initial game state with standard starting position
 */
export function createInitialGameState(): GameState {
  const state: GameState = {
    board: createStartingBoard(),
    activeColor: C.White,
    castlingRights: {
      whiteKingside: true,
      whiteQueenside: true,
      blackKingside: true,
      blackQueenside: true,
    },
    enPassantSquare: null,
    halfmoveClock: 0,
    fullmoveNumber: 1,
    positionHistory: [],
  };
  
  // Add initial position to history
  state.positionHistory.push(getPositionHash(state));
  
  return state;
}

/**
 * Converts a square index (0-63) to algebraic notation (e.g., "e4")
 */
export function squareToAlgebraic(square: Square): string {
  const file = String.fromCharCode(97 + (square % 8)); // 'a' + file
  const rank = Math.floor(square / 8) + 1;
  return `${file}${rank}`;
}

/**
 * Converts algebraic notation (e.g., "e4") to square index (0-63)
 */
export function algebraicToSquare(algebraic: string): Square {
  const file = algebraic.charCodeAt(0) - 97; // 'a' = 0
  const rank = parseInt(algebraic[1]) - 1;
  return rank * 8 + file;
}

/**
 * Gets the rank (row) of a square (0-7)
 */
export function getRank(square: Square): number {
  return Math.floor(square / 8);
}

/**
 * Gets the file (column) of a square (0-7)
 */
export function getFile(square: Square): number {
  return square % 8;
}

/**
 * Checks if a square is on the board (0-63)
 */
export function isValidSquare(square: number): square is Square {
  return square >= 0 && square < 64;
}

/**
 * Parses a FEN string and returns a GameState
 * FEN format: <pieces> <active> <castling> <enPassant> <halfmove> <fullmove>
 * Example: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
 */
export function parseFEN(fen: string): GameState {
  const parts = fen.trim().split(/\s+/);
  if (parts.length !== 6) {
    throw new Error('Invalid FEN: must have 6 parts separated by spaces');
  }

  const [piecePlacement, activeColor, castling, enPassant, halfmove, fullmove] = parts;

  // Parse board
  const board = parseFENBoard(piecePlacement);

  // Parse active color
  if (activeColor !== 'w' && activeColor !== 'b') {
    throw new Error('Invalid FEN: active color must be "w" or "b"');
  }

  // Parse castling rights
  const castlingRights = {
    whiteKingside: castling.includes('K'),
    whiteQueenside: castling.includes('Q'),
    blackKingside: castling.includes('k'),
    blackQueenside: castling.includes('q'),
  };

  // Parse en passant square
  let enPassantSquare: Square | null = null;
  if (enPassant !== '-') {
    enPassantSquare = algebraicToSquare(enPassant);
  }

  // Parse halfmove clock and fullmove number
  const halfmoveClock = parseInt(halfmove);
  const fullmoveNumber = parseInt(fullmove);

  if (isNaN(halfmoveClock) || isNaN(fullmoveNumber)) {
    throw new Error('Invalid FEN: halfmove and fullmove must be numbers');
  }

  const state: GameState = {
    board,
    activeColor: activeColor === 'w' ? C.White : C.Black,
    castlingRights,
    enPassantSquare,
    halfmoveClock,
    fullmoveNumber,
    positionHistory: [], // Initialize empty, will add current position next
  };
  
  // Add current position to history
  state.positionHistory.push(getPositionHash(state));
  
  return state;
}

/**
 * Parses the piece placement part of a FEN string
 * @internal
 */
function parseFENBoard(piecePlacement: string): (Piece | null)[] {
  const board: (Piece | null)[] = createEmptyBoard();
  const ranks = piecePlacement.split('/');

  if (ranks.length !== 8) {
    throw new Error('Invalid FEN: piece placement must have 8 ranks');
  }

  // Process from rank 8 (index 0) to rank 1 (index 7)
  for (let rankIndex = 0; rankIndex < 8; rankIndex++) {
    const rank = ranks[rankIndex];
    let fileIndex = 0;

    for (const char of rank) {
      if (fileIndex >= 8) {
        throw new Error(`Invalid FEN: rank ${8 - rankIndex} has too many files`);
      }

      // Check if it's a digit (empty squares)
      if (/\d/.test(char)) {
        const emptyCount = parseInt(char);
        if (emptyCount < 1 || emptyCount > 8) {
          throw new Error('Invalid FEN: empty square count must be 1-8');
        }
        fileIndex += emptyCount;
      } else {
        // It's a piece
        const piece = pieceFromFEN(char);
        if (!piece) {
          throw new Error(`Invalid FEN: unknown piece character "${char}"`);
        }

        // Calculate square index: rank 8 = squares 56-63, rank 1 = squares 0-7
        const squareIndex = (7 - rankIndex) * 8 + fileIndex;
        board[squareIndex] = piece;
        fileIndex++;
      }
    }

    if (fileIndex !== 8) {
      throw new Error(`Invalid FEN: rank ${8 - rankIndex} has ${fileIndex} files, expected 8`);
    }
  }

  return board;
}

/**
 * Converts a GameState to a FEN string
 */
export function toFEN(state: GameState): string {
  const piecePlacement = boardToFEN(state.board);
  const activeColor = state.activeColor === C.White ? 'w' : 'b';
  const castling = castlingRightsToFEN(state.castlingRights);
  const enPassant = state.enPassantSquare !== null ? squareToAlgebraic(state.enPassantSquare) : '-';
  const halfmove = state.halfmoveClock.toString();
  const fullmove = state.fullmoveNumber.toString();

  return `${piecePlacement} ${activeColor} ${castling} ${enPassant} ${halfmove} ${fullmove}`;
}

/**
 * Converts a board to FEN piece placement notation
 * @internal
 */
function boardToFEN(board: (Piece | null)[]): string {
  const ranks: string[] = [];

  // Process from rank 8 (squares 56-63) to rank 1 (squares 0-7)
  for (let rank = 7; rank >= 0; rank--) {
    let rankStr = '';
    let emptyCount = 0;

    for (let file = 0; file < 8; file++) {
      const square = rank * 8 + file;
      const piece = board[square];

      if (piece === null) {
        emptyCount++;
      } else {
        if (emptyCount > 0) {
          rankStr += emptyCount.toString();
          emptyCount = 0;
        }
        rankStr += getPieceFEN(piece);
      }
    }

    // Add any remaining empty squares
    if (emptyCount > 0) {
      rankStr += emptyCount.toString();
    }

    ranks.push(rankStr);
  }

  return ranks.join('/');
}

/**
 * Converts castling rights to FEN notation
 * @internal
 */
function castlingRightsToFEN(rights: GameState['castlingRights']): string {
  let result = '';
  if (rights.whiteKingside) result += 'K';
  if (rights.whiteQueenside) result += 'Q';
  if (rights.blackKingside) result += 'k';
  if (rights.blackQueenside) result += 'q';
  return result || '-';
}

/**
 * Generates a unique hash for a position for three-fold repetition detection
 * Two positions are the same if they have:
 * - Same pieces on same squares
 * - Same active color
 * - Same castling rights
 * - Same en passant square
 */
export function getPositionHash(state: GameState): string {
  // Use board FEN + active color + castling + en passant
  // We don't include halfmove clock or fullmove number as they don't affect position identity
  const piecePlacement = boardToFEN(state.board);
  const activeColor = state.activeColor === C.White ? 'w' : 'b';
  const castling = castlingRightsToFEN(state.castlingRights);
  const enPassant = state.enPassantSquare !== null ? squareToAlgebraic(state.enPassantSquare) : '-';
  
  return `${piecePlacement} ${activeColor} ${castling} ${enPassant}`;
}


